import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { templatesApi, type Template, type TemplateRequest } from '../services/api';
import { formatDate } from '../lib/utils';
import { useToast } from '../components/ui/use-toast';
import ConfirmDialog from '../components/ConfirmDialog';

const TemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<TemplateRequest>({
    name: '',
    description: '',
    instructions: '',
    is_default: false,
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const { toast } = useToast();

  const loadTemplates = async () => {
    try {
      const templateList = await templatesApi.getTemplates();
      setTemplates(templateList);
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      description: '',
      instructions: '',
      is_default: false,
    });
    setIsCreating(true);
    setIsEditing(false);
    setSelectedTemplate(null);
  };

  const handleEdit = (template: Template) => {
    setFormData({
      name: template.name,
      description: template.description || '',
      instructions: template.instructions,
      is_default: template.is_default,
    });
    setSelectedTemplate(template);
    setIsEditing(true);
    setIsCreating(false);
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        await templatesApi.createTemplate(formData);
        await loadTemplates();
        setIsCreating(false);
        setIsEditing(false);
        toast({ title: 'Template created' });
      } else if (selectedTemplate) {
        await templatesApi.updateTemplate(selectedTemplate.id, formData);
        await loadTemplates();
        setIsEditing(false);
        const updatedTemplates = await templatesApi.getTemplates();
        const updatedSelected = updatedTemplates.find(t => t.id === selectedTemplate.id);
        setSelectedTemplate(updatedSelected || null);
        toast({ title: 'Template saved' });
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      toast({ variant: 'destructive', title: 'Failed to save template', description: 'Please try again.' });
    }
  };

  const handleDelete = (template: Template) => {
    setTemplateToDelete(template);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;
    try {
      await templatesApi.deleteTemplate(templateToDelete.id);
      setTemplates(templates.filter(t => t.id !== templateToDelete.id));
      if (selectedTemplate?.id === templateToDelete.id) {
        setSelectedTemplate(null);
      }
      toast({ title: `Deleted "${templateToDelete.name}"` });
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast({ variant: 'destructive', title: 'Failed to delete template', description: 'Please try again.' });
    } finally {
      setConfirmOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsCreating(false);
    if (!isCreating && selectedTemplate) {
      setFormData({
        name: selectedTemplate.name,
        description: selectedTemplate.description || '',
        instructions: selectedTemplate.instructions,
        is_default: selectedTemplate.is_default,
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Templates</h1>
          <p className="text-muted-foreground mt-2">
            Create and manage note templates to customise your output format
          </p>
        </div>
        <Button onClick={handleCreate}>
          Create Template
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Templates List */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Available Templates</h2>
          {templates.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-muted-foreground">No templates found. Create your first template!</p>
              </CardContent>
            </Card>
          ) : (
            templates.map((template) => (
              <Card 
                key={template.id} 
                className={`cursor-pointer transition-colors ${
                  selectedTemplate?.id === template.id ? 'ring-2 ring-primary' : 'hover:bg-accent'
                }`}
                onClick={() => {
                  setSelectedTemplate(template);
                  setIsEditing(false);
                  setIsCreating(false);
                }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {template.name}
                      {template.is_default && (
                        <span className="ml-2 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                          Default
                        </span>
                      )}
                    </CardTitle>
                  </div>
                  {template.description && (
                    <CardDescription>{template.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      Created {formatDate(template.created_at)}
                    </span>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(template);
                        }}
                      >
                        Edit
                      </Button>
                      {!template.is_default && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(template);
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Template Details/Editor */}
        <div>
          <h2 className="text-xl font-semibold mb-4">
            {isCreating ? 'Create Template' : isEditing ? 'Edit Template' : 'Template Details'}
          </h2>
          
          {(isEditing || isCreating) ? (
            <Card>
              <CardHeader>
                <CardTitle>{isCreating ? 'New Template' : 'Edit Template'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Template name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Brief description of the template"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Instructions</label>
                  <textarea
                    value={formData.instructions}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    rows={12}
                    placeholder="Detailed instructions for how the AI should format the notes..."
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    className="rounded border-input"
                  />
                  <label htmlFor="is_default" className="text-sm font-medium">
                    Mark as default template
                  </label>
                </div>

                <div className="flex space-x-2">
                  <Button onClick={handleSave} disabled={!formData.name || !formData.instructions}>
                    Save Template
                  </Button>
                  <Button variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : selectedTemplate ? (
            <Card>
              <CardHeader>
                <CardTitle>
                  {selectedTemplate.name}
                  {selectedTemplate.is_default && (
                    <span className="ml-2 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                      Default
                    </span>
                  )}
                </CardTitle>
                {selectedTemplate.description && (
                  <CardDescription>{selectedTemplate.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Instructions</h4>
                  <div className="bg-muted p-4 rounded-md">
                    <pre className="whitespace-pre-wrap text-sm">{selectedTemplate.instructions}</pre>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">
                    Created: {formatDate(selectedTemplate.created_at)}
                  </p>
                  {selectedTemplate.updated_at && (
                    <p className="text-sm text-muted-foreground">
                      Updated: {formatDate(selectedTemplate.updated_at)}
                    </p>
                  )}
                </div>

                <div className="flex space-x-2">
                  <Button onClick={() => handleEdit(selectedTemplate)}>
                    Edit Template
                  </Button>
                  {!selectedTemplate.is_default && (
                    <Button variant="outline" onClick={() => handleDelete(selectedTemplate)}>
                      Delete Template
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-muted-foreground">Select a template to view details or create a new one</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={templateToDelete ? `Delete "${templateToDelete.name}"?` : 'Delete template?'}
        description="This action cannot be undone."
        confirmText="Delete"
        variant="destructive"
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default TemplatesPage;
